import React, { useEffect, useState } from 'react';
import { Container, Typography, TextField, Button, Box, Avatar, CircularProgress, Paper, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { School } from '../types';

const AdminSettings: React.FC = () => {
  const { user } = useAuth();
  const [school, setSchool] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [form, setForm] = useState({ 
    name: '', 
    address: '', 
    contact_info: '', 
    contact_email: '', 
    attendance_mode: 'session' 
  });
  const [logo, setLogo] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (!user?.school_id) return;
    setLoading(true);
    api.get(`/schools/${user.school_id}`)
      .then(res => {
        setSchool(res.data as School);
        setForm({
          name: (res.data as any).name || '',
          address: (res.data as any).address || '',
          contact_info: (res.data as any).contact_info || '',
          contact_email: (res.data as any).contact_email || '',
          attendance_mode: (res.data as any).attendance_mode || 'session',
        });
        setLogoUrl((res.data as any).logo || null);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load school info');
        setLoading(false);
      });
  }, [user?.school_id]);

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/schools/${user.school_id}`, form);
      setSchool({ ...school, ...form });
    } catch {
      setError('Failed to update school info');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setLogo(e.target.files[0]);
    }
  };

  const handleLogoUpload = async () => {
    if (!user || !logo) return;
    setLogoUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', logo);
    try {
      const res = await api.post(`/schools/${user.school_id}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl((res.data as any).logo);
      setSchool({ ...school, logo: (res.data as any).logo });
      setLogo(null);
    } catch {
      setError('Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  if (!user || user.role !== 'admin') {
    return <Container><Typography>You do not have access to this page.</Typography></Container>;
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>School Settings</Typography>
        {loading ? <CircularProgress /> : (
          <>
            <TextField
              label="School Name"
              name="name"
              value={form.name}
              onChange={handleTextFieldChange}
              fullWidth
              margin="normal"
              placeholder="Enter school name"
              helperText={!form.name ? 'No name set. Please enter the school name.' : ''}
            />
            <TextField
              label="Address"
              name="address"
              value={form.address}
              onChange={handleTextFieldChange}
              fullWidth
              margin="normal"
              placeholder="Enter address"
              helperText={!form.address ? 'No address set. Please enter the school address.' : ''}
            />
            <TextField
              label="Contact Info"
              name="contact_info"
              value={form.contact_info}
              onChange={handleTextFieldChange}
              fullWidth
              margin="normal"
              placeholder="Enter contact info (e.g. phone, email)"
              helperText={!form.contact_info ? 'No contact info set. Please enter contact details.' : ''}
            />
            <TextField
              label="Contact Email"
              name="contact_email"
              type="email"
              value={form.contact_email}
              onChange={handleTextFieldChange}
              fullWidth
              margin="normal"
              placeholder="Enter contact email for invoices"
              helperText={!form.contact_email ? 'No contact email set for invoices.' : ''}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Attendance Mode</InputLabel>
              <Select
                name="attendance_mode"
                value={form.attendance_mode}
                onChange={handleSelectChange}
                label="Attendance Mode"
              >
                <MenuItem value="session">Session-based</MenuItem>
                <MenuItem value="day">Day-based</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Avatar src={logoUrl || undefined} sx={{ width: 64, height: 64, mr: 2 }} />
              <Button variant="contained" component="label" disabled={logoUploading}>
                Upload Logo
                <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
              </Button>
              {logo && (
                <Button onClick={handleLogoUpload} disabled={logoUploading} sx={{ ml: 2 }}>
                  {logoUploading ? <CircularProgress size={20} /> : 'Save Logo'}
                </Button>
              )}
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              sx={{ mt: 3 }}
            >
              {saving ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
            {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AdminSettings; 